import { createTheme } from '@mui/material/styles';

const autofillStyles = (theme, { bg }) => ({
  'input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill': {
    WebkitTextFillColor: `${theme.palette.text.primary} !important`,
    caretColor: `${theme.palette.text.primary} !important`,
    WebkitBoxShadow: `0 0 0 1000px ${bg} inset !important`,
    boxShadow: `0 0 0 1000px ${bg} inset !important`,
    transition: 'background-color 0s ease 0s !important',
    fontSize: '18px !important',
    lineHeight: '20px !important',
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
});

const sharedComponentOverrides = {
  MuiPaper: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        backgroundImage: 'none',
        backgroundColor: 'var(--surface-bg)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      contained: {
        boxShadow: 'none',
      },
      outlined: {
        boxShadow: 'none',
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        marginTop: 8,
        borderRadius: 20,
        border: '1px solid rgba(148, 163, 184, 0.24)',
        boxShadow: '0 22px 60px rgba(15, 23, 42, 0.16)',
        overflow: 'hidden',
        backgroundImage: 'none',
      },
    },
  },
  MuiPickersLayout: {
    styleOverrides: {
      root: {
        minWidth: 318,
        background: 'linear-gradient(180deg, var(--surface-elev), var(--surface-bg) 38%)',
      },
      contentWrapper: {
        padding: '2px 10px 12px',
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        paddingLeft: 18,
        paddingRight: 12,
        marginTop: 12,
        marginBottom: 6,
      },
      label: {
        fontWeight: 900,
        color: 'var(--text-color)',
        textTransform: 'capitalize',
      },
      switchViewButton: {
        color: 'var(--muted-color)',
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        width: 34,
        height: 34,
        borderRadius: 12,
        color: 'var(--muted-color)',
        transition: 'all 140ms ease',
        '&:hover': {
          color: '#2563EB',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
        },
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: {
        color: 'var(--muted-color)',
        fontSize: 12,
        fontWeight: 800,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        width: 36,
        height: 36,
        borderRadius: 12,
        fontWeight: 750,
        color: 'var(--text-color)',
        transition: 'all 140ms ease',
        '&:hover': {
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
        },
        '&.Mui-selected': {
          color: '#fff',
          backgroundColor: '#2563EB',
          boxShadow: '0 8px 18px rgba(37, 99, 235, 0.24)',
          '&:hover': {
            backgroundColor: '#1D4ED8',
          },
        },
        '&.MuiPickersDay-today': {
          borderColor: 'rgba(37, 99, 235, 0.55)',
        },
      },
    },
  },
  MuiMonthCalendar: {
    styleOverrides: {
      root: {
        gap: 8,
        padding: '8px 14px 16px',
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        height: 42,
        borderRadius: 14,
        fontWeight: 850,
        color: 'var(--text-color)',
        transition: 'all 140ms ease',
        '&:hover': {
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
        },
        '&.Mui-selected': {
          color: '#fff',
          backgroundColor: '#2563EB',
          boxShadow: '0 10px 22px rgba(37, 99, 235, 0.24)',
          '&:hover': {
            backgroundColor: '#1D4ED8',
          },
        },
      },
    },
  },
  MuiYearCalendar: {
    styleOverrides: {
      root: {
        padding: '8px 14px 16px',
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      yearButton: {
        borderRadius: 14,
        fontWeight: 850,
        color: 'var(--text-color)',
        transition: 'all 140ms ease',
        '&:hover': {
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
        },
        '&.Mui-selected': {
          color: '#fff',
          backgroundColor: '#2563EB',
          boxShadow: '0 10px 22px rgba(37, 99, 235, 0.24)',
          '&:hover': {
            backgroundColor: '#1D4ED8',
          },
        },
      },
    },
  },
};

export const availableThemes = {
  light: {
    id: 'light',
    name: 'Светлая',
    muiTheme: createTheme({
      palette: {
        mode: 'light',
        background: {
          default: '#F4F6FB',
          paper: '#FFFFFF',
        },
        primary: { main: '#2563EB' },
        secondary: { main: '#0EA5A4' },
        text: { primary: '#0B1220', secondary: '#475569' },
      },
      typography: {
        fontFamily: 'Manrope, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      },
      components: {
        ...sharedComponentOverrides,
        MuiCssBaseline: {
          styleOverrides: (theme) => ({
            ...autofillStyles(theme, {
              bg: 'rgba(242,243,251,0.8)',
            }),
          }),
        },
        MuiOutlinedInput: {
          styleOverrides: {
            input: {
              fontSize: 18,
              lineHeight: '20px',
            },
          },
        },
      },
    }),
    cssVars: {
      '--app-bg': '#F4F6FB',
      '--app-gradient':
        'radial-gradient(1200px 600px at 15% 0%, rgba(37, 99, 235, 0.10) 0%, rgba(244, 246, 251, 0.6) 55%, rgba(244, 246, 251, 0.1) 100%)',
      '--surface-bg': '#FFFFFF',
      '--surface-elev': '#F8FAFF',
      '--text-color': '#0B1220',
      '--muted-color': '#64748B',
      '--border-color': 'rgba(15, 23, 42, 0.10)',
      '--shadow-color': 'rgba(15, 23, 42, 0.08)',
      '--sidebar-border': 'rgba(219, 224, 236, 0.9)',
      '--sidebar-bg': '#FCFDFF',
      '--sidebar-text': '#0B1220',
      '--sidebar-muted': '#475569',
      '--sidebar-hover': 'rgba(15, 23, 42, 0.06)',
      '--accent': '#2563EB',
      '--accent-2': '#0EA5A4',
    },
  },
  dark: {
    id: 'dark',
    name: 'Тёмная',
    muiTheme: createTheme({
      palette: {
        mode: 'dark',
        background: {
          default: '#0B1120',
          paper: '#111827',
        },
        primary: { main: '#60A5FA' },
        secondary: { main: '#2DD4BF' },
        text: { primary: '#E2E8F0', secondary: '#94A3B8' },
      },
      typography: {
        fontFamily: 'Manrope, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      },
      components: {
        ...sharedComponentOverrides,
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 14,
              fontWeight: 500,
              color: '#E2E8F0',
            },
            icon: {
              color: 'inherit',
              opacity: 0.9,
            },
            standardError: {
              backgroundColor: 'rgba(248, 113, 113, 0.16)',
              border: '1px solid rgba(248, 113, 113, 0.35)',
              color: '#FEE2E2',
            },
            standardWarning: {
              backgroundColor: 'rgba(251, 191, 36, 0.16)',
              border: '1px solid rgba(251, 191, 36, 0.35)',
              color: '#FEF3C7',
            },
            standardInfo: {
              backgroundColor: 'rgba(56, 189, 248, 0.16)',
              border: '1px solid rgba(56, 189, 248, 0.35)',
              color: '#E0F2FE',
            },
            standardSuccess: {
              backgroundColor: 'rgba(34, 197, 94, 0.16)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              color: '#DCFCE7',
            },
            outlinedError: {
              borderColor: 'rgba(248, 113, 113, 0.45)',
              color: '#FEE2E2',
            },
            outlinedWarning: {
              borderColor: 'rgba(251, 191, 36, 0.45)',
              color: '#FEF3C7',
            },
            outlinedInfo: {
              borderColor: 'rgba(56, 189, 248, 0.45)',
              color: '#E0F2FE',
            },
            outlinedSuccess: {
              borderColor: 'rgba(34, 197, 94, 0.45)',
              color: '#DCFCE7',
            },
            filledError: {
              backgroundColor: 'rgba(248, 113, 113, 0.88)',
              color: '#0B1120',
            },
            filledWarning: {
              backgroundColor: 'rgba(251, 191, 36, 0.88)',
              color: '#0B1120',
            },
            filledInfo: {
              backgroundColor: 'rgba(56, 189, 248, 0.88)',
              color: '#0B1120',
            },
            filledSuccess: {
              backgroundColor: 'rgba(34, 197, 94, 0.88)',
              color: '#0B1120',
            },
          },
        },
      },
    }),
    cssVars: {
      '--app-bg': '#0B1120',
      '--app-gradient':
        'radial-gradient(1200px 600px at 15% 0%, rgba(96, 165, 250, 0.10) 0%, rgba(11, 17, 32, 0.9) 55%, rgba(11, 17, 32, 0.6) 100%)',
      '--surface-bg': '#111827',
      '--surface-elev': '#0F172A',
      '--text-color': '#E2E8F0',
      '--muted-color': '#94A3B8',
      '--border-color': 'rgba(148, 163, 184, 0.20)',
      '--shadow-color': 'rgba(2, 6, 23, 0.6)',
      '--sidebar-bg': '#0F172A',
      '--sidebar-text': '#E2E8F0',
      '--sidebar-muted': '#94A3B8',
      '--sidebar-border': 'rgba(148, 163, 184, 0.20)',
      '--sidebar-hover': 'rgba(148, 163, 184, 0.12)',
      '--accent': '#60A5FA',
      '--accent-2': '#2DD4BF',
    },
  },
};
